import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Building2, Store, ShoppingBag, Euro, TrendingUp, XCircle, CheckCircle2, Ban, Settings2, Mail, UserPlus, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import DashboardShell, { StatCard, EmptyState } from "@/components/DashboardShell";
import OrderCard from "@/components/OrderCard";
import { eur } from "@/lib/status";
import api, { apiError } from "@/lib/api";
import { downloadPdf } from "@/lib/download";

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [bnbs, setBnbs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [settings, setSettings] = useState(null);

  const loadAll = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/partners?status=pending").then(({ data }) => setPending(data)).catch(() => {});
    api.get("/admin/bnbs").then(({ data }) => setBnbs(data)).catch(() => {});
    api.get("/admin/vendors").then(({ data }) => setVendors(data)).catch(() => {});
    api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/admin/leads").then(({ data }) => setLeads(data)).catch(() => {});
    api.get("/admin/contacts").then(({ data }) => setContacts(data)).catch(() => {});
    api.get("/admin/settings").then(({ data }) => setSettings(data)).catch(() => {});
  };
  useEffect(loadAll, []);

  const action = async (userId, act) => {
    try { await api.post(`/admin/partners/${userId}/${act}`); toast.success("Aggiornato"); loadAll(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveSettings = async (patch) => {
    try { const { data } = await api.put("/admin/settings", patch); setSettings(data); toast.success("Impostazioni salvate"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const tabs = [
    { key: "overview", label: "Panoramica" },
    { key: "pending", label: "In attesa", badge: pending.length },
    { key: "bnbs", label: "B&B" },
    { key: "vendors", label: "Locali" },
    { key: "orders", label: "Ordini" },
    { key: "leads", label: "Lead ospiti" },
    { key: "contacts", label: "Contatti" },
    { key: "settings", label: "Impostazioni" },
  ];

  return (
    <DashboardShell title="Dashboard Amministratore" subtitle="Panoramica generale MorningBox" tabs={tabs} active={tab} onTab={setTab}>
      {tab === "overview" && stats && (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => downloadPdf(`/admin/settlement.pdf?period=month`, "riepilogo-morningbox-mese.pdf")} variant="outline" className="rounded-full" data-testid="admin-settlement-month"><Download className="w-4 h-4 mr-1" />Riepilogo mese (PDF)</Button>
            <Button onClick={() => downloadPdf(`/admin/settlement.pdf?period=week`, "riepilogo-morningbox-settimana.pdf")} variant="outline" className="rounded-full" data-testid="admin-settlement-week"><Download className="w-4 h-4 mr-1" />Riepilogo settimana (PDF)</Button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Partner in attesa" value={stats.pending_partners} icon={Users} />
            <StatCard label="B&B attivi" value={stats.active_bnbs} icon={Building2} />
            <StatCard label="Locali attivi" value={stats.active_vendors} icon={Store} />
            <StatCard label="Ordini oggi" value={stats.orders_today} icon={ShoppingBag} />
            <StatCard label="Ordini totali" value={stats.orders_month} icon={ShoppingBag} />
            <StatCard label="Valore ordini" value={eur(stats.total_value)} icon={Euro} />
            <StatCard label="Commissioni MorningBox" value={eur(stats.commission)} icon={TrendingUp} />
            <StatCard label="Ordini annullati" value={stats.cancelled} icon={XCircle} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-navy/10 p-5">
              <p className="font-semibold text-navy mb-3">Ripartizione economica totale</p>
              <div className="space-y-2 text-sm">
                <Line k="Importi dovuti ai locali" v={eur(stats.vendor_due)} />
                <Line k="Margini generati per i B&B" v={eur(stats.bnb_margins)} />
                <Line k="Commissioni MorningBox" v={eur(stats.commission)} />
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-navy/10 p-5">
              <p className="font-semibold text-navy mb-3">Pacchetti più venduti</p>
              {stats.top_packages.map(([name, count]) => (
                <div key={name} className="flex justify-between text-sm py-1"><span className="text-navy/60 capitalize">{name || "—"}</span><span className="font-medium text-navy">{count} ordini</span></div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "pending" && (
        <div className="space-y-3">
          {pending.length === 0 && <EmptyState icon={CheckCircle2} text="Nessun partner in attesa di approvazione" />}
          {pending.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl border border-navy/10 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`pending-${p.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <Badge className={p.role === "bnb" ? "bg-navy" : "bg-orange"}>{p.role === "bnb" ? "B&B" : "Locale"}</Badge>
                  <p className="font-semibold text-navy">{p.profile?.name}</p>
                </div>
                <p className="text-sm text-navy/50 mt-1">{p.profile?.city} · {p.email} · {p.profile?.phone}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => action(p.id, "approve")} className="rounded-full bg-successtext hover:opacity-90 text-white" data-testid={`approve-${p.id}`}><CheckCircle2 className="w-4 h-4 mr-1" />Approva</Button>
                <Button size="sm" variant="outline" onClick={() => action(p.id, "reject")} className="rounded-full text-errortext border-errortext/30" data-testid={`reject-${p.id}`}><XCircle className="w-4 h-4 mr-1" />Rifiuta</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "bnbs" && <PartnerList items={bnbs} onSuspend={(uid) => action(uid, "suspend")} onReactivate={(uid) => action(uid, "reactivate")} type="B&B" />}
      {tab === "vendors" && <PartnerList items={vendors} onSuspend={(uid) => action(uid, "suspend")} onReactivate={(uid) => action(uid, "reactivate")} type="Locale" />}

      {tab === "orders" && (
        <div className="grid md:grid-cols-2 gap-4">
          {orders.length === 0 && <EmptyState text="Nessun ordine" />}
          {orders.map((o) => <OrderCard key={o.id} order={o} showEconomics />)}
        </div>
      )}

      {tab === "leads" && (
        <div className="space-y-2">
          {leads.length === 0 && <EmptyState text="Nessun lead" />}
          {leads.map((l) => (
            <div key={l.id} className="bg-white rounded-2xl border border-navy/10 p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-cream flex items-center justify-center"><UserPlus className="w-4 h-4 text-orange" /></div>
              <div className="flex-1"><p className="font-medium text-navy text-sm">{l.name} · {l.city}</p><p className="text-xs text-navy/50">{l.email} {l.phone && `· ${l.phone}`}</p></div>
              {l.is_demo && <Badge variant="outline" className="text-xs">demo</Badge>}
            </div>
          ))}
        </div>
      )}

      {tab === "contacts" && (
        <div className="space-y-2">
          {contacts.length === 0 && <EmptyState text="Nessun messaggio" />}
          {contacts.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl border border-navy/10 p-4">
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-orange" /><p className="font-medium text-navy text-sm">{c.name} {c.business && `· ${c.business}`}</p><Badge variant="outline" className="text-xs">{c.request_type}</Badge></div>
              <p className="text-sm text-navy/60 mt-2">{c.message}</p>
              <p className="text-xs text-navy/40 mt-1">{c.email} {c.phone && `· ${c.phone}`} {c.city && `· ${c.city}`}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "settings" && settings && (
        <div className="max-w-lg space-y-5">
          <div className="bg-white rounded-2xl border border-navy/10 p-5 flex items-center justify-between">
            <div>
              <p className="font-semibold text-navy flex items-center gap-2"><Settings2 className="w-4 h-4 text-orange" />Modalità pre-lancio</p>
              <p className="text-sm text-navy/50 mt-1">Se attiva, mostra l'avviso di selezione partner in homepage.</p>
            </div>
            <Switch checked={settings.prelaunch_mode} onCheckedChange={(v) => saveSettings({ prelaunch_mode: v })} data-testid="prelaunch-toggle" />
          </div>
          <div className="bg-white rounded-2xl border border-navy/10 p-5">
            <Label>Commissione MorningBox (€ a persona)</Label>
            <div className="flex gap-2 mt-2">
              <Input type="number" step="0.01" defaultValue={settings.commission_pp} id="comm" className="rounded-xl" data-testid="commission-input" />
              <Button onClick={() => saveSettings({ commission_pp: Number(document.getElementById("comm").value) })} className="rounded-full bg-navy text-white">Salva</Button>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-navy/10 p-5">
            <Label>Zone operative</Label>
            <div className="flex flex-wrap gap-2 mt-2">{settings.cities?.map((c) => <Badge key={c} variant="outline">{c}</Badge>)}</div>
            <div className="flex gap-2 mt-3">
              <Input id="newcity" placeholder="Aggiungi città" className="rounded-xl" data-testid="city-input" />
              <Button onClick={() => { const v = document.getElementById("newcity").value.trim(); if (v) { saveSettings({ cities: [...settings.cities, v] }); document.getElementById("newcity").value = ""; } }} className="rounded-full bg-navy text-white">Aggiungi</Button>
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

function Line({ k, v }) { return <div className="flex justify-between"><span className="text-navy/60">{k}</span><span className="font-bold text-navy">{v}</span></div>; }

function PartnerList({ items, onSuspend, onReactivate, type }) {
  if (!items.length) return <EmptyState text={`Nessun ${type}`} />;
  return (
    <div className="space-y-2">
      {items.map((p) => (
        <div key={p.id} className="bg-white rounded-2xl border border-navy/10 p-4 flex items-center justify-between gap-3">
          <div>
            <p className="font-medium text-navy">{p.name} {p.is_demo && <Badge variant="outline" className="text-xs ml-1">demo</Badge>}</p>
            <p className="text-xs text-navy/50">{p.city} · {p.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={p.status === "active" ? "bg-successtext text-white" : p.status === "suspended" ? "bg-errortext text-white" : "bg-slate-400 text-white"}>{p.status}</Badge>
            {p.status === "active"
              ? <Button size="sm" variant="outline" onClick={() => onSuspend(p.user_id)} className="rounded-full text-errortext border-errortext/30"><Ban className="w-4 h-4" /></Button>
              : <Button size="sm" variant="outline" onClick={() => onReactivate(p.user_id)} className="rounded-full text-successtext border-successtext/30"><CheckCircle2 className="w-4 h-4" /></Button>}
          </div>
        </div>
      ))}
    </div>
  );
}
